import { useCallback, useEffect, useRef, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import { listSubscriptions } from "./requests/subscriptionsApi";
import type { SubscriptionListQuery, SubscriptionListResponse } from "./types";

/**
 * Loads one page of subscriptions (CTX-26).
 *
 * Paging, filtering and sorting are server-side: the page owns the query and
 * this hook refetches whenever it changes. That's deliberate — the table is
 * cross-tenant for a Global Admin, so the full set can be large and filtering
 * it in the browser would mean shipping every tenant's subscriptions to the
 * client.
 *
 * No tenant lens is applied on purpose. Unlike the per-service views, this is
 * an explicitly cross-tenant admin table with its own Customer column and
 * filter. Access is scoped server-side by the caller's tenant hierarchy, so
 * when partners are enabled they see their own tree without any change here.
 */
export function useSubscriptions(query: SubscriptionListQuery): {
	data: SubscriptionListResponse | null;
	loading: boolean;
	error: string | null;
	hasFetched: boolean;
	refresh: () => Promise<void>;
} {
	const api = useAxiosAuth0();
	const [data, setData] = useState<SubscriptionListResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [hasFetched, setHasFetched] = useState(false);

	// Guards against a slow earlier request resolving after a newer one and
	// overwriting fresher rows — easy to trigger by typing in the search box.
	const requestIdRef = useRef(0);

	const fetchPage = useCallback(async () => {
		const requestId = ++requestIdRef.current;
		setLoading(true);
		try {
			const response = await listSubscriptions(api, query);
			if (requestId !== requestIdRef.current) return;
			setData(response);
			setError(null);
		} catch (err) {
			if (requestId !== requestIdRef.current) return;
			setError(parseAxiosError(err).message);
		} finally {
			if (requestId === requestIdRef.current) {
				setLoading(false);
				setHasFetched(true);
			}
		}
		// `query` is rebuilt by the page via useMemo, so identity changes only
		// when a field actually changes.
	}, [api, query]);

	useEffect(() => {
		void fetchPage();
	}, [fetchPage]);

	return { data, loading, error, hasFetched, refresh: fetchPage };
}
